import { Component, EventEmitter, Input, Output, booleanAttribute } from '@angular/core';

import { inject } from '@angular/core';
import { LocaleService } from '../../../services/locale.service';
import { ModalComponent } from '../modal/modal.component';

/**
 * Confirmation modal with optional danger styling, inline error banner and
 * a spinner on the confirm button while `busy` is true.
 *
 *   <app-confirm-modal
 *     [open]="deleteOpen()"
 *     [busy]="deleting()"
 *     [error]="deleteError()"
 *     title="Supprimer cet utilisateur ?"
 *     [confirmLabel]="'Supprimer définitivement'"
 *     danger
 *     (confirm)="confirmDelete()"
 *     (close)="closeDelete()">
 *     L'utilisateur <b>{{ fullName() }}</b> sera supprimé et ne pourra plus se connecter.
 *   </app-confirm-modal>
 */
@Component({
  selector: 'app-confirm-modal',
  imports: [ModalComponent],
  template: `
    <app-modal
      [isOpen]="open"
      (close)="onClose()"
      className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-dark"
    >
      <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ t(title) }}</h3>
      <div class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        <ng-content></ng-content>
      </div>

      @if (error) {
        <div class="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
          {{ error }}
        </div>
      }

      <div class="mt-6 flex justify-end gap-2">
        <button
          type="button"
          (click)="onClose()"
          [disabled]="busy"
          class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
        >
          {{ t(cancelLabel) }}
        </button>
        <button
          type="button"
          (click)="confirm.emit()"
          [disabled]="busy"
          [class]="confirmButtonClasses"
        >
          @if (busy) {
            <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
              <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
            </svg>
            {{ t(busyLabel ?? confirmLabel) }}
          } @else {
            {{ t(confirmLabel) }}
          }
        </button>
      </div>
    </app-modal>
  `,
})
export class ConfirmModalComponent {
  private readonly locale = inject(LocaleService);

  /** Résout une **clé** i18n (repli sur la clé brute — un texte déjà final passe
   *  sans dommage, migration incrémentale comme page-header/data-state). */
  t(v: string | undefined): string {
    return v ? this.locale.t(v) : '';
  }
  @Input({ required: true }) open!: boolean;
  @Input({ required: true }) title!: string;
  @Input({ transform: booleanAttribute }) busy = false;
  @Input() error: string | null = null;
  @Input() confirmLabel = 'common.confirm';
  @Input() cancelLabel = 'common.cancel';
  /** Label shown next to the spinner while `busy` is true. */
  @Input() busyLabel?: string;
  /** Use the destructive red palette for the confirm button. Accepts the
   *  attribute-only form `<app-confirm-modal danger>`. */
  @Input({ transform: booleanAttribute }) danger = false;

  @Output() readonly confirm = new EventEmitter<void>();
  @Output() readonly close = new EventEmitter<void>();

  onClose(): void {
    if (this.busy) return;
    this.close.emit();
  }

  get confirmButtonClasses(): string {
    const base =
      'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60';
    return this.danger
      ? `${base} bg-error-500 hover:bg-error-600`
      : `${base} bg-brand-500 hover:bg-brand-600 disabled:bg-brand-300`;
  }
}
